import { useState } from "react";
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Avatar,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../iconify";
import Detail from "./Detail";
import { fDateTime } from "../../utils/formatTime";

// Up-to-two initials, splitting on whitespace and dots ("E. Fusco" / "syed.belall" -> "EF" / "SB").
function getInitials(name) {
  if (!name) return "?";
  const parts = name
    .trim()
    .split(/[\s.]+/)
    .filter(Boolean);
  const letters = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("");
  return (letters || name[0] || "?").toUpperCase();
}

// Plain-language, "sealed lottery" explanation of the provably-fair draw (matches the RNG
// contract: 256-bit seed -> HMAC-SHA256 fingerprint per entrant -> lowest digest wins).
const HOW_IT_WORKS = [
  {
    icon: "mdi:dice-5-outline",
    title: "A single random seed",
    rest: " is generated by the system - 256 bits, nobody chooses it.",
  },
  {
    icon: "mdi:fingerprint",
    title: "Every eligible entrant gets a fingerprint",
    rest: " - HMAC-SHA256 of the seed and their user ID.",
  },
  {
    icon: "mdi:arrow-down-bold-outline",
    title: "The lowest fingerprint wins",
    rest: " (argmin). Full-digest comparison means no favourites and no rounding bias.",
  },
  {
    icon: "mdi:lock-outline",
    title: "The seed is published",
    rest: ", so anyone can repeat these steps and land on the same winner.",
  },
];

function HowItWorksItem({ icon, title, rest }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Iconify icon={icon} width={24} sx={{ color: "text.secondary", flexShrink: 0, mt: 0.25 }} />
      <Typography variant="body2">
        <strong>{title}</strong>
        {rest}
      </Typography>
    </Stack>
  );
}

function IdField({ label, value, mono }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={700} sx={{ fontFamily: mono ? "monospace" : undefined }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Right-hand panel for a recorded draw, as a 3-tab card:
 *   Result       - the winner, when it was drawn, and the pool size.
 *   How it works - plain-language provably-fair explanation.
 *   Verify       - algorithm, pool size and seed, with copy-to-clipboard so a
 *                  third party can recompute the winner.
 */
export default function DrawResultCard({
  result,
  competitionName,
  seasonLabel,
  periodLabel,
}) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  const handleCopySeed = async () => {
    if (!result.seed) return;
    try {
      await navigator.clipboard.writeText(result.seed);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context); the seed stays visible to copy manually.
    }
  };

  const poolCount = result.eligible_count;
  const subtitle = [seasonLabel, periodLabel].filter(Boolean).join(" · ");
  // username is nullable; fall back to ref_id, then user_id, so there is always a label.
  const displayName =
    result.winner_username || result.winner_ref_id || result.winner_user_id;

  return (
    <Card>
      <Box sx={{ px: 3, pt: 3 }}>
        <Typography variant="h6">{competitionName || "Draw result"}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ px: 2, mt: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Result" />
        <Tab label="How it works" />
        <Tab label="Verify" />
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tab === 0 && (
          <Box>
            {result.already_existed && (
              <Alert severity="info" sx={{ mb: 2 }}>
                A draw already existed; showing the recorded result.
              </Alert>
            )}
            <Typography variant="overline" color="text.secondary">
              Winner
            </Typography>

            <Box
              sx={(theme) => ({
                mt: 1,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                bgcolor: alpha(theme.palette.success.main, 0.08),
              })}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={(theme) => ({
                    width: 56,
                    height: 56,
                    fontWeight: 700,
                    color: theme.palette.success.dark,
                    bgcolor: alpha(theme.palette.success.main, 0.2),
                  })}
                >
                  {getInitials(displayName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2, wordBreak: "break-word" }}
                  >
                    {displayName}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ color: "success.main", mt: 0.5 }}
                  >
                    <Iconify icon="mdi:check" width={18} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Selected winner
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              <Divider
                sx={(theme) => ({
                  my: 2,
                  borderColor: alpha(theme.palette.success.main, 0.25),
                })}
              />

              <Stack direction="row" spacing={6} flexWrap="wrap" useFlexGap>
                <IdField label="Engagecraft ID" value={result.winner_engagecraft_id} />
                <IdField label="Ref ID" value={result.winner_ref_id} mono />
              </Stack>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Drawn {fDateTime(result.drawn_at) || "-"} · {poolCount} eligible entrant
              {poolCount === 1 ? "" : "s"}
            </Typography>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Think of it as a sealed lottery no one can rig:
            </Typography>
            <Stack spacing={2}>
              {HOW_IT_WORKS.map((item) => (
                <HowItWorksItem key={item.title} {...item} />
              ))}
            </Stack>
          </Box>
        )}

        {tab === 2 && (
          <Box>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Detail label="Algorithm" mono>
                {result.algorithm_version}
              </Detail>
              <Detail label="Eligible pool">
                {poolCount} (sorted, snapshotted)
              </Detail>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Seed
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                mb: 2,
                p: 1.5,
                bgcolor: "grey.100",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 13,
                wordBreak: "break-all",
              }}
            >
              {result.seed}
            </Box>
            <Button variant="contained" onClick={handleCopySeed}>
              {copied ? "Copied!" : "Copy seed for third-party verification"}
            </Button>
          </Box>
        )}
      </Box>
    </Card>
  );
}
